import { Upload, X } from 'lucide-react'
import { useState, useRef, type ChangeEvent } from 'react'

import { Button } from '@/components/ui/Button'

type ImageUploadProps = {
  currentImageUrl?: string | null
  onImageChange: (file: File | null) => void
  error?: string
  label?: string
  description?: string
}

export function ImageUpload({
  currentImageUrl,
  onImageChange,
  error,
  label = '画像をアップロード',
  description = 'PNG、JPG、GIF、WebP形式（最大5MB）',
}: ImageUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      onImageChange(file)
    }
  }

  const handleRemove = () => {
    setPreview(null)
    onImageChange(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {preview ? (
          <div className="relative h-24 w-24 overflow-hidden rounded-lg border-2 border-white/10">
            <img src={preview} alt="Preview" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute right-1 top-1 rounded-full bg-red-500 p-1 text-white hover:bg-red-600"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-white/20 bg-white/5">
            <Upload className="h-8 w-8 text-white/40" />
          </div>
        )}

        <div className="flex-1 space-y-2">
          <Button type="button" variant="secondary" onClick={handleClick}>
            <Upload className="mr-2 h-4 w-4" />
            {label}
          </Button>
          <p className="text-xs text-white/50">{description}</p>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}
